import { ReactNode } from 'react'
import { navigate } from 'gatsby'
import clsx from 'clsx'
import { ColumnsType, Table, TableProps, Tag } from 'components/common'
import { CustomerType } from 'lib/graphql/storeadmin'

export interface Customer
  extends Record<string, string | number | boolean | ReactNode> {
  no?: number
  name?: string
  corporateName?: string
  email?: string
  phone?: string
  address?: string
  numberOfReservations?: number
  bookableFlag?: boolean
  createdAt?: string
  type?: string
  action?: ReactNode
}

type InternalProps = TableProps<Customer>
type Actions = Pick<InternalProps, 'onChange' | 'onRow'>
export interface CustomersTableProps
  extends Omit<
    InternalProps,
    'pagination' | 'columns' | 'rowClassName' | 'sticky' | 'onChange' | 'onRow'
  > {
  dataSource: Array<Customer>
  customerType?: CustomerType
}

const commonColumns = [
  {
    key: 'no',
    title: 'No.',
    dataIndex: 'no',
    width: 80,
    render: (value) => `#${value}`,
  },
  {
    key: 'bookableFlag',
    title: '予約可否',
    dataIndex: 'bookableFlag',
    width: 110,
    render: (bookableFlag: boolean) => {
      if (bookableFlag) {
        return (
          <Tag style={{ textAlign: 'center' }} color="success">
            予約可能
          </Tag>
        )
      } else {
        return (
          <Tag style={{ textAlign: 'center' }} color="error">
            予約不可
          </Tag>
        )
      }
    },
  },
  {
    key: 'name',
    title: '顧客名',
    dataIndex: 'name',
    ellipsis: true,
  },
]

const commonDetailsColumns = [
  {
    key: 'address',
    title: '住所',
    dataIndex: 'address',
    ellipsis: true,
  },
  {
    key: 'email',
    title: 'メールアドレス',
    dataIndex: 'email',
    ellipsis: true,
  },
  {
    key: 'phone',
    title: '電話番号',
    dataIndex: 'phone',
  },
]

const columns = (customerType: CustomerType): ColumnsType<Customer> => {
  if (customerType === CustomerType.Corporate) {
    return [
      ...commonColumns,
      {
        key: 'personInCharge',
        title: '担当者',
        dataIndex: 'personInCharge',
        ellipsis: true,
      },
      ...commonDetailsColumns,
    ]
  } else if (customerType === CustomerType.Personal) {
    return [...commonColumns, ...commonDetailsColumns]
  } else {
    return [
      {
        key: 'email',
        title: 'メールアドレス',
        dataIndex: 'email',
        ellipsis: true,
      },
      {
        key: 'createdAt',
        title: 'アカウント作成日時',
        dataIndex: 'createdAt',
        ellipsis: true,
      },
    ]
  }
}

export const CustomersTable = ({
  dataSource,
  customerType,
  ...restProps
}: CustomersTableProps): JSX.Element => {
  const onRow: Actions['onRow'] = (record) => {
    return {
      onClick: async () => {
        await navigate(`./${record.key}`)
      },
    }
  }

  return (
    <>
      <Table<Customer>
        sticky
        pagination={false}
        rowClassName={clsx('cursor-pointer')}
        dataSource={dataSource}
        columns={columns(customerType)}
        onRow={onRow}
        {...restProps}
      />
    </>
  )
}
